import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Newspaper, Podcast, BookOpen, Library } from "lucide-react";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import { NAV } from "@/components/Sidebar";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";

const CONTENT_SECTIONS = [
  { key: "stories",  heading: "News Stories",   icon: Newspaper, to: () => "/dashboard/news-ingestion" },
  { key: "podcasts", heading: "Podcast Episodes", icon: Podcast, to: () => "/dashboard/podcasts" },
  { key: "docs",     heading: "Editorial Docs", icon: BookOpen,  to: (r) => `/dashboard/writers-desk?doc=${r.id}` },
  { key: "library",  heading: "Content Library", icon: Library,  to: () => "/dashboard/library" },
];

/** ⌘K / Ctrl+K quick-jump palette — every nav destination + station content. */
export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();
  const t = useT();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Debounced content search across stories / podcasts / docs / library
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!open || q.trim().length < 2) { setResults(null); return; }
    debounceRef.current = setTimeout(() => {
      api.get(`/search/global?q=${encodeURIComponent(q.trim())}`)
        .then((r) => setResults(r.data))
        .catch(() => setResults(null));
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q, open]);

  useEffect(() => { if (!open) { setQ(""); setResults(null); } }, [open]);

  const groups = useMemo(() => {
    const out = [];
    const home = { label: "Home", items: [] };
    NAV.forEach((n) => {
      if (!n.children) { home.items.push(n); return; }
      const items = [];
      if (n.to) items.push({ ...n, label: `${n.label} — Overview` });
      n.children.forEach((c) => {
        if (c.children) {
          c.children.forEach((g) => items.push(g));
        } else {
          items.push(c);
        }
      });
      out.push({ label: n.label, items });
    });
    return [home, ...out];
  }, []);

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="command-palette-trigger"
        className="mr-auto inline-flex items-center gap-2 rounded-md border border-line bg-bg-elevated/60 px-3 py-1.5 text-xs text-ink-muted hover:text-ink-primary hover:border-brand-amber/40 transition"
      >
        <Search className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">{t("Jump to any tool…")}</span>
        <kbd className="ml-1 rounded border border-line bg-bg-surface px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider">⌘K</kbd>
      </button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Search tools, stories, podcasts, docs…"
          data-testid="command-palette-input"
          value={q}
          onValueChange={setQ}
        />
        <CommandList data-testid="command-palette-list" className="max-h-[420px]">
          <CommandEmpty>Nothing found.</CommandEmpty>

          {/* Content results — from the live search endpoint */}
          {results && CONTENT_SECTIONS.map((sec) => {
            const rows = results[sec.key] || [];
            if (!rows.length) return null;
            return (
              <CommandGroup key={sec.key} heading={sec.heading}>
                {rows.map((r) => (
                  <CommandItem
                    key={`${sec.key}-${r.id}`}
                    value={`${r.label} ${r.id} ${q}`}
                    onSelect={() => go(sec.to(r))}
                    data-testid={`cmdk-content-${sec.key}-${r.id}`}
                    className="cursor-pointer"
                  >
                    <sec.icon className="mr-2 h-4 w-4 text-brand-amber" />
                    <span className="flex-1 truncate">{r.label}</span>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">{r.hint || sec.heading}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            );
          })}

          {/* Tool destinations — filtered client-side by cmdk */}
          {groups.map((g) => (
            <CommandGroup key={g.label} heading={g.label}>
              {g.items.map((item) => (
                <CommandItem
                  key={`${g.label}-${item.to}-${item.label}`}
                  value={`${item.label} ${g.label}`}
                  onSelect={() => go(item.to)}
                  data-testid={`cmdk-${item.testid}`}
                  className="cursor-pointer"
                >
                  <item.icon className="mr-2 h-4 w-4 text-ink-muted" />
                  <span className="flex-1">{item.label}</span>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">{g.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}
